import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";

const FAQ = [
  {
    question: "What is solar fleet monitoring?",
    answer:
      "Solar fleet monitoring is the practice of viewing and managing many residential solar and battery systems from one interface. Installers use it to see real-time status, overnight reserve risk, battery health, and alert activity across all sites instead of logging into each inverter or OEM portal separately.",
  },
  {
    question: "How is this different from inverter monitoring?",
    answer:
      "Inverter monitoring typically shows one system per vendor app. Watt Tower aggregates multi-vendor telemetry into a single installer dashboard, adds overnight reserve forecasting, battery capacity tracking, installer-controlled alerts, and fleet-level reports. You see energy in kWh with explicit confidence, not just percentages from one OEM.",
  },
  {
    question: "Can I monitor off-grid systems?",
    answer:
      "Yes. Watt Tower supports off-grid and hybrid systems. We focus on residential solar and battery fleets, including systems that are fully off-grid or that use grid as backup. Monitoring and forecasting work the same; we flag overnight reserve risk and battery health regardless of grid connection.",
  },
  {
    question: "How does overnight forecasting work?",
    answer:
      "Watt Tower uses historical telemetry (load, PV, battery) to project energy available at sunrise. You see projected kWh at sunrise with best, expected, and worst scenarios and an explicit confidence level (High, Medium, Low). We never imply false precision; confidence improves over 30–90 days as the system learns.",
  },
];

export default function SolarMonitoringSoftwareForInstallers() {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return (
    <PublicLayout>
      <Helmet>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      </Helmet>
      <article className="mx-auto max-w-3xl px-4 py-16 lg:px-8">
        <h1 className="mb-6 font-sans text-3xl font-bold md:text-4xl">
          Solar Monitoring Software for Installers
        </h1>
        <p className="mb-10 font-sans text-lg leading-relaxed text-wt-text-secondary">
          One installer dashboard for real-time solar and battery monitoring,
          overnight reserve forecasting, and fleet-level visibility. Built for
          companies that install and maintain residential solar and hybrid
          systems.
        </p>

        <section className="space-y-10">
          <h2 className="font-sans text-2xl font-semibold text-wt-text-primary">
            Why Installers Need Fleet-Level Visibility
          </h2>
          <p className="font-sans text-base leading-relaxed text-wt-text-secondary">
            When you manage dozens or hundreds of systems, single-site OEM apps
            do not scale. You need solar fleet monitoring: one view of status,
            risk, and health across every site. Watt Tower gives installers
            fleet-level visibility so you can see which systems are live,
            which are at overnight reserve risk, and which need attention
            without opening multiple portals or waiting for customer calls.
          </p>

          <h2 className="font-sans text-2xl font-semibold text-wt-text-primary">
            Real-Time Solar and Battery Monitoring
          </h2>
          <p className="font-sans text-base leading-relaxed text-wt-text-secondary">
            Watt Tower pulls telemetry from supported inverters (Sol-Ark, EG4,
            SolarEdge) and shows real-time solar production, battery state,
            and load in one place. Data is shown in kWh where it matters;
            confidence is stated, not implied. You see Live, Stale, and
            Offline status so you know when a system has stopped reporting
            before the homeowner does.
          </p>

          <h2 className="font-sans text-2xl font-semibold text-wt-text-primary">
            Forecasting Overnight Reserve Risk
          </h2>
          <p className="font-sans text-base leading-relaxed text-wt-text-secondary">
            One of the biggest blind spots for installers is whether a system
            will have enough energy by sunrise. Our solar monitoring software
            forecasts projected kWh at sunrise with explicit confidence bands.
            Fleet-level “At Risk” flags highlight sites that may run low
            overnight, so you can decide to notify the customer or schedule a
            check instead of reacting after a 2 a.m. call.
          </p>

          <h2 className="font-sans text-2xl font-semibold text-wt-text-primary">
            Multi-Site Fleet Dashboard
          </h2>
          <p className="font-sans text-base leading-relaxed text-wt-text-secondary">
            The installer dashboard summarizes all sites: how many are online,
            how many are at overnight risk, and how many are offline. Filter by
            provider, tag, or grid mode. Drill into any property for
            telemetry, battery health, and alert history. Generate availability
            and incident reports for customers or internal ops. Multi-site
            monitoring replaces scattered logins with one operational view.
          </p>

          <h2 className="font-sans text-2xl font-semibold text-wt-text-primary">
            Built for Sol-Ark and Hybrid Systems
          </h2>
          <p className="font-sans text-base leading-relaxed text-wt-text-secondary">
            Watt Tower supports Sol-Ark, EG4, and SolarEdge. We focus on
            residential solar and battery systems, including hybrid and
            off-grid. Credentials are read-only, encrypted, and revocable. We
            observe; we do not command. Your solar monitoring software should
            give you clarity without adding control risk.
          </p>
        </section>

        <section className="mt-16 border-t border-wt-border-default pt-12">
          <h2 className="mb-8 font-sans text-2xl font-semibold text-wt-text-primary">
            Frequently Asked Questions
          </h2>
          <dl className="space-y-6">
            {FAQ.map((item) => (
              <div key={item.question}>
                <dt className="font-sans text-base font-semibold text-wt-text-primary">
                  {item.question}
                </dt>
                <dd className="mt-2 font-sans text-base leading-relaxed text-wt-text-secondary">
                  {item.answer}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <div className="mt-12 flex flex-wrap gap-4">
          <a href="/#installer-access">
            <Button className="h-11 bg-primary px-6 font-sans font-semibold text-wt-bg-primary hover:bg-wt-green-muted">
              Request Installer Access
            </Button>
          </a>
          <Link to="/">
            <Button
              variant="outline"
              className="h-11 border-wt-border-default bg-transparent font-sans text-wt-text-primary hover:bg-white/5"
            >
              Back to home
            </Button>
          </Link>
        </div>
        <p className="mt-6 font-sans text-sm text-wt-text-muted">
          See also:{" "}
          <Link
            to="/solar-fleet-dashboard"
            className="text-primary hover:underline"
          >
            Solar fleet dashboard
          </Link>
          ,{" "}
          <Link
            to="/battery-backup-monitoring"
            className="text-primary hover:underline"
          >
            battery backup monitoring
          </Link>
          ,{" "}
          <Link
            to="/solar-forecasting-software"
            className="text-primary hover:underline"
          >
            solar forecasting software
          </Link>
          .
        </p>
      </article>
    </PublicLayout>
  );
}
